import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface TruncatedTextProps {
  text: string
  /** px. A real per-column value, so it stays an inline style (Frontend §2.2). */
  maxWidth: number
}

/** Single-line text with an ellipsis. Hovering shows the full text, but only when it's actually cut off. */
export function TruncatedText({ text, maxWidth }: TruncatedTextProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const [tip, setTip] = useState<{ x: number; y: number; below: boolean } | null>(null)
  const show = () => {
    const el = ref.current
    if (!el || el.scrollWidth <= el.clientWidth) return
    const r = el.getBoundingClientRect()
    const below = r.top < 120
    setTip({
      x: Math.min(Math.max(r.left + r.width / 2, 170), window.innerWidth - 170),
      y: below ? r.bottom + 8 : r.top - 8,
      below,
    })
  }
  return (
    <>
      {/* The full text is in the DOM, so screen readers read it all; the tooltip is a visual aid only. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <span
        ref={ref}
        className="truncate"
        style={{ maxWidth }}
        onMouseEnter={show}
        onMouseLeave={() => {
          setTip(null)
        }}
      >
        {text}
      </span>
      {tip &&
        createPortal(
          <div
            className={`text-tip ${tip.below ? 'below' : ''}`}
            style={{ left: tip.x, top: tip.y }}
            role="tooltip"
            aria-hidden
          >
            {text}
          </div>,
          document.body,
        )}
    </>
  )
}
